/**
 * Control groups and keyboard shortcuts (U14, R22).
 *
 * Bindings match Red Alert 2 where it had them: Q/W/E/R for the four sidebar
 * tabs, 0–9 for control groups with Ctrl to assign, A attack-move, S stop,
 * G guard, X scatter, H cycle idle harvester, C centre on selection, and Space
 * centre on the last event.
 *
 * **C and Space are distinct actions** and both are required — centring on your
 * own selection and jumping to where something just happened are different
 * needs, and a game that conflates them loses one of them.
 *
 * Control groups hold entity **ids**, so recalling a group after members die
 * yields the survivors rather than whatever recycled their slots.
 */

import type { Subsystem } from '../core/registry.js'
import { Entities, EF, ST } from '../sim/entities.js'
import type { Ctx } from '../core/registry.js'

export const GROUP_COUNT = 10

export class ControlGroups implements Subsystem {
  static id = 'groups'
  static deps = ['entities'] as const

  private entities!: Entities
  private readonly groups: number[][] = Array.from({ length: GROUP_COUNT }, () => [])

  /** Cell of the last notable event, for Space. */
  lastEventX = -1
  lastEventY = -1

  init(ctx: Ctx): void {
    this.entities = ctx.get<Entities>('entities')
    // Base-under-attack and unit-lost are what a player wants Space to find.
    ctx.on('actor:damaged', (p) => this.noteEvent(p as { id: number }))
    ctx.on('actor:death', (p) => {
      const q = p as { x: number; y: number }
      this.lastEventX = q.x
      this.lastEventY = q.y
    })
  }

  private noteEvent(p: { id: number }): void {
    const slot = this.entities.resolve(p.id)
    if (slot < 0) return
    if (!this.entities.has(slot, EF.Structure)) return
    this.lastEventX = this.entities.x[slot]
    this.lastEventY = this.entities.y[slot]
  }

  assign(group: number, ids: readonly number[]): void {
    if (group < 0 || group >= GROUP_COUNT) return
    this.groups[group] = [...ids].sort((a, b) => a - b)
  }

  /** Members still alive. Dead members drop out rather than resolving stale. */
  recall(group: number): number[] {
    if (group < 0 || group >= GROUP_COUNT) return []
    const alive = this.groups[group].filter((id) => this.entities.isLive(id))
    this.groups[group] = alive
    return [...alive]
  }

  size(group: number): number {
    return this.recall(group).length
  }

  clear(): void {
    for (let i = 0; i < GROUP_COUNT; i++) this.groups[i] = []
  }

  /**
   * The next idle harvester after `afterId`, wrapping. Returns 0 if there is
   * none — which is itself useful feedback that the economy is saturated.
   */
  nextIdleHarvester(owner: number, afterId: number): number {
    const idle: number[] = []
    for (let s = 0; s < this.entities.limit; s++) {
      if (!this.entities.isAlive(s)) continue
      if (this.entities.owner[s] !== owner) continue
      if (!this.entities.has(s, EF.Harvester)) continue
      const state = this.entities.state[s]
      if (state === ST.Idle || state === ST.Scattering) idle.push(this.entities.idAt(s))
    }
    if (idle.length === 0) return 0
    idle.sort((a, b) => a - b)
    for (const id of idle) if (id > afterId) return id
    return idle[0]
  }
}
